import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { HiMinusCircle, HiSparkles } from "react-icons/hi";
import { Box, Flex, Text } from "rebass";
import { SecondaryButton } from "../styledComponents";
import { IStorageStatus } from "../@types";

interface NoStorageProps {
  storageStatus: IStorageStatus;
  onCancel: () => void;
  manageMode?: boolean;
}

export const NoStorage = ({
  storageStatus,
  onCancel,
  manageMode = false,
}: NoStorageProps) => {
  const history = useHistory();
  const percentage = (storageStatus.savedCount / storageStatus.limit) * 100;

  const handleUpgrade = () => {
    onCancel();
    history.push("/upgrade");
  };

  const handleFreeUpClips = () => {
    onCancel();
    history.push("/library");
  };

  return (
    <>
      <Box p="24px 28px">
        <Box mb="24px">
          <StorageHeader mb="10px">
            <StorageLabel>Your Storage</StorageLabel>
            <StorageCount>
              <EnvyCount>{storageStatus.savedCount}</EnvyCount> /{" "}
              {storageStatus.limit} clips
            </StorageCount>
          </StorageHeader>
          <ProgressBar>
            <ProgressFill $percentage={percentage} />
          </ProgressBar>
        </Box>

        {!manageMode && (
          <BodyText mb="16px">To save this clip, you can:</BodyText>
        )}

        <OptionCard onClick={handleFreeUpClips} mb="12px">
          <Flex alignItems="center" style={{ gap: "14px" }}>
            <IconWrapper $variant="remove">
              <HiMinusCircle size={20} />
            </IconWrapper>
            <div>
              <OptionTitle>Free up clips from your storage</OptionTitle>
              <OptionDescription>
                Clips will revert to drafts and be subject to expiration
                policies
              </OptionDescription>
            </div>
          </Flex>
        </OptionCard>

        <OptionCard onClick={handleUpgrade}>
          <Flex alignItems="center" style={{ gap: "14px" }}>
            <IconWrapper $variant="upgrade">
              <HiSparkles size={20} />
            </IconWrapper>
            <div>
              <OptionTitle>Upgrade for more storage</OptionTitle>
              <OptionDescription>
                Get more space with a Pro plan
              </OptionDescription>
            </div>
          </Flex>
        </OptionCard>
      </Box>

      <ModalFooter>
        <SecondaryButton onClick={onCancel}>
          {manageMode ? "Close" : "Cancel"}
        </SecondaryButton>
      </ModalFooter>
    </>
  );
};

const ModalFooter = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 28px;
`;

const StorageHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const StorageLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.shade200};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
`;

const StorageCount = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 18px;
  font-weight: 600;
`;

const EnvyCount = styled.span`
  color: ${({ theme }) => theme.colors.envy};
`;

const ProgressBar = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 9px;
  height: 18px;
  overflow: hidden;
  width: 100%;
`;

const ProgressFill = styled(Box)<{ $percentage: number }>`
  background: ${({ theme, $percentage }) => {
    if ($percentage >= 90) return theme.colors.alert;
    if ($percentage >= 70) return theme.colors.caution;
    return theme.colors.envy;
  }};
  border-radius: 9px;
  height: 100%;
  transition: width 0.3s ease;
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
`;

const BodyText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade200};
  font-family: Barlow;
  font-size: 14px;
`;

const IconWrapper = styled(Box)<{ $variant: "remove" | "upgrade" }>`
  align-items: center;
  background: ${({ theme, $variant }) =>
    $variant === "upgrade" ? theme.colors.envy + "20" : theme.colors.navy50};
  border-radius: 10px;
  color: ${({ theme, $variant }) =>
    $variant === "upgrade" ? theme.colors.envy : theme.colors.shade300};
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  width: 40px;
`;

const OptionCard = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  padding: 16px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const OptionTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
`;

const OptionDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 13px;
`;
